import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Cookie, Settings, BarChart3, Shield, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CookiesPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="section-container">
          {/* Header */}
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">
                  Cookie Policy
                </h1>
                <p className="text-muted-foreground">Last updated: January 2025</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                This Cookie Policy explains how Blue Lion Law Limited uses cookies and similar technologies 
                on our website. By using our website, you consent to the use of cookies as described in this policy.
              </p>

              {/* What Are Cookies */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <Cookie className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    What Are Cookies?
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>
                    Cookies are small text files that are placed on your device when you visit a website. 
                    They are widely used to make websites work more efficiently and to provide information 
                    to website owners.
                  </p>
                </div>
              </section>

              {/* Types of Cookies */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <Settings className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    Types of Cookies We Use
                  </h2>
                </div>
                <div className="pl-8 space-y-6 text-foreground/80">
                  {/* Essential Cookies */}
                  <div className="p-4 rounded-lg bg-muted/50 border border-border">
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-success" />
                      Essential Cookies
                    </h3>
                    <p className="mb-2">
                      These cookies are necessary for the website to function properly. They enable basic 
                      functions like page navigation and access to secure areas of the website.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Cannot be disabled</strong> - The website cannot function properly without these cookies.
                    </p>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="p-4 rounded-lg bg-muted/50 border border-border">
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-accent" />
                      Analytics Cookies
                    </h3>
                    <p className="mb-2">
                      These cookies help us understand how visitors interact with our website by collecting 
                      and reporting information anonymously. This helps us improve our website.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Can be disabled</strong> - You can opt out of analytics cookies.
                    </p>
                  </div>

                  {/* Functional Cookies */}
                  <div className="p-4 rounded-lg bg-muted/50 border border-border">
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Settings className="w-4 h-4 text-gold" />
                      Functional Cookies
                    </h3>
                    <p className="mb-2">
                      These cookies enable enhanced functionality and personalisation, such as remembering 
                      your preferences and chat history.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Can be disabled</strong> - Some features may not work if disabled.
                    </p>
                  </div>
                </div>
              </section>

              {/* Cookie Table */}
              <section className="mb-10">
                <h2 className="font-heading text-xl font-bold text-foreground mb-4">
                  Cookies We Use
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Cookie Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Purpose</th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Duration</th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr>
                        <td className="px-4 py-3 text-foreground/80">session_id</td>
                        <td className="px-4 py-3 text-foreground/80">Maintains your session</td>
                        <td className="px-4 py-3 text-foreground/80">Session</td>
                        <td className="px-4 py-3 text-foreground/80">Essential</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-foreground/80">chat_session</td>
                        <td className="px-4 py-3 text-foreground/80">Maintains chat history</td>
                        <td className="px-4 py-3 text-foreground/80">24 hours</td>
                        <td className="px-4 py-3 text-foreground/80">Functional</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-foreground/80">_ga</td>
                        <td className="px-4 py-3 text-foreground/80">Google Analytics - distinguishes users</td>
                        <td className="px-4 py-3 text-foreground/80">2 years</td>
                        <td className="px-4 py-3 text-foreground/80">Analytics</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-foreground/80">cookie_consent</td>
                        <td className="px-4 py-3 text-foreground/80">Stores your cookie preferences</td>
                        <td className="px-4 py-3 text-foreground/80">1 year</td>
                        <td className="px-4 py-3 text-foreground/80">Essential</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Managing Cookies */}
              <section className="mb-10">
                <h2 className="font-heading text-xl font-bold text-foreground mb-4">
                  Managing Cookies
                </h2>
                <div className="space-y-4 text-foreground/80">
                  <p>
                    You can control and manage cookies in several ways:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <strong>Browser settings:</strong> Most browsers allow you to refuse or accept cookies, 
                      delete existing cookies, and set preferences for certain websites.
                    </li>
                    <li>
                      <strong>Our cookie banner:</strong> When you first visit our website, you can choose 
                      which optional cookies to accept.
                    </li>
                  </ul>
                  <p>
                    Please note that if you choose to block cookies, some parts of our website may not 
                    function properly.
                  </p>
                </div>
              </section>

              {/* Contact */}
              <section className="mt-12 p-6 rounded-xl bg-muted/50 border border-border">
                <h2 className="font-heading text-xl font-bold text-foreground mb-4">
                  Contact Us
                </h2>
                <p className="text-foreground/80 mb-4">
                  If you have any questions about our use of cookies, please contact us:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-accent" />
                    <a href="mailto:contactus@bluelionclaims.co.uk" className="text-accent hover:underline">
                      contactus@bluelionclaims.co.uk
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-accent" />
                    <a href="tel:02081530033" className="text-accent hover:underline">
                      0208 153 0033
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CookiesPage;
